/**
 * Route guard for the C2 front-half Workboard (C2-WORKBOARD-UI-BUILD-01).
 * Mirrors RequireRoutingSettings / RequireNavSection: the pure decision lives
 * in decideWorkboardAccess (workboard-view.ts) and keys off the caller's role -
 * receptionist / coc (the front-half operators). Any other role redirects to
 * its own homePath. This is a role-only gate: NO /me flag signal, NO feature
 * flag, NO new nav section (so no backend mirror / drift-test change).
 *
 * Backend route auth (the /api/me/work-actions and /api/leads/:id/stage scope +
 * role checks) remains the real security boundary; this guard is UX cleanliness
 * only.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideWorkboardAccess } from '@/dashboard/workboard/workboard-view';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireWorkboardViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireWorkboardView({
  loading,
  error,
  me,
  children,
}: RequireWorkboardViewProps): JSX.Element | null {
  const decision = decideWorkboardAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireWorkboard(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireWorkboardView
      loading={ctx.loading}
      error={ctx.error}
      me={ctx.me}
    />
  );
}
